import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { appInfo } from "../config/appInfo.const";
import { getSecureItem, setSecureItem, removeSecureItem } from "../utils/LocalStorageManager";
import { apiUrls } from "../config/apiUrls.const";

const BASE_URL: any = appInfo.baseUrl;

// Create a global Axios instance
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: true
});

// ✅ Request interceptor to attach Bearer token if it exists
apiClient.interceptors.request.use((config) => {
  // Uses access_token to align with the new token rotation standard
  const token = getSecureItem("access_token");
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));


// =========================================================================
// 🔄 REFRESH TOKEN ENGINE & QUEUE CHANNELS
// =========================================================================
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

function handleForceLogout() {
  removeSecureItem("access_token");
  removeSecureItem("refresh_token");
  removeSecureItem("user");
  removeSecureItem("businesses");
  removeSecureItem("binfo");
  
  window.location.href = "/";
}


// =========================================================================
// ⚡ RESPONSE INTERCEPTOR WITH AUTOMATIC RETRY LOGIC
// =========================================================================
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response) {
      // Prevent infinite loops if refresh fails with 401
      if (originalRequest.url?.includes(apiUrls.refresh)) {
        handleForceLogout();
        return Promise.reject(error);
      }

      // Handle 401 Unauthorized
      if (error.response.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return apiClient(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshToken = getSecureItem("refresh_token");
          if (!refreshToken) {
            throw new Error("No refresh token available");
          }

          // Request a new token using native clean axios instance
          const res = await axios.post(`${BASE_URL}${apiUrls.refresh}`, {}, {
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
              "Authorization": `Bearer ${refreshToken}`
            },
            withCredentials: true
          });

          if (res.data?.success) {
            const { access_token, refresh_token } = res.data.data;

            setSecureItem("access_token", access_token);
            setSecureItem("refresh_token", refresh_token);

            processQueue(null, access_token);

            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          handleForceLogout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      if (error.response.status === 403) {
        console.error("Forbidden: You don’t have access to this resource.");
      }
      if (error.response.status >= 500) {
        console.error("Server error, please try again later.");
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;


// =========================================================================
// 🚀 REUSABLE HTTP METHODS WITH YOUR IMAGE ENGINES PRESERVED
// =========================================================================
const _get = async <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return apiClient.get<T>(url, config);
};

// 🔥 PRESERVED: Your precise adaptive multi-media upload handler
const _post = async <T = any>(
  url: string,
  data?: any,
  config: AxiosRequestConfig = {}
): Promise<AxiosResponse<T>> => {
  let finalData = data;
  const finalConfig: AxiosRequestConfig = { ...config };

  // Detect if data includes any File or Blob
  const hasFile =
    data instanceof FormData ||
    (data &&
      Object.values(data).some(
        (value) =>
          value instanceof File ||
          value instanceof Blob ||
          (Array.isArray(value) && value.some((v) => v instanceof File))
      ));

  if (hasFile) {
    if (!(data instanceof FormData)) {
      const formData = new FormData();
      for (const key in data) {
        if (Array.isArray(data[key])) {
          data[key].forEach((v: any) => formData.append(`${key}[]`, v));
        } else {
          formData.append(key, data[key]);
        }
      }
      finalData = formData;
    }

    finalConfig.headers = {
      ...(config.headers || {}),
      "Content-Type": "multipart/form-data",
    };
  }

  return apiClient.post<T>(url, finalData, finalConfig);
};

const _put = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return apiClient.put<T>(url, data, config);
};

const _update = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return apiClient.patch<T>(url, data, config);
};

const _delete = async <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return apiClient.delete<T>(url, config);
};

export { _get, _post, _put, _update, _delete };

export const cleanApiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: true // Fixes the local CORS cookie/credential handshakes
});